import type { FileUploadSession } from "@/db/fileUploadDb";
import { useAuthSessionStore } from "@/stores/authSession";

/**
 * LSP-22950 O8: which organization and user the CURRENT auth session belongs to, for the upload drain's scope check.
 *
 * A durable upload row mints its server target lazily, under whatever session is active when the drain picks it up.
 * After an organization switch (or a different user signing in on the same browser) that would upload a file added
 * for one organization into another. Rows are stamped with the organization and user that added them
 * (`FileUploadDialog` → `batchOrganizationId` / `batchUserId`); the drain holds a row whose stamp does not match.
 */
export interface AdminUploadScope {
	organizationId: string | null;
	userId: string | null;
	generation?: number;
}

/** `undefined` when the auth store cannot be read here (no active Pinia) — the caller then cannot judge scope. */
export function readCurrentAdminUploadScope(): AdminUploadScope | undefined {
	try {
		const authSession = useAuthSessionStore();
		return {
			organizationId: authSession.organizationId ?? null,
			userId: authSession.userId ?? null,
			generation: authSession.identityGeneration
		};
	} catch {
		return undefined;
	}
}

export type AdminUploadScopeMismatch = "organization" | "user";

/**
 * Why `session` must be held under `scope`, or `undefined` when it may upload. A row with no stamp (added before the
 * stamp existed, or before its batch metadata was written) carries no evidence either way and is not held. A stamped
 * row is held while the current session is unknown (claims not loaded yet), because uploading it then could still be
 * the wrong identity; it resumes on the next pass once the claims match.
 */
export function adminUploadScopeMismatch(
	session: Pick<FileUploadSession, "batchOrganizationId" | "batchUserId">,
	scope: AdminUploadScope
): AdminUploadScopeMismatch | undefined {
	if (session.batchOrganizationId && session.batchOrganizationId !== scope.organizationId) return "organization";
	if (session.batchUserId && session.batchUserId !== scope.userId) return "user";
	return undefined;
}
